import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { ConversationModal, fmtDate, fmtPhone } from "../shared/ConversationModal";

// ── types ─────────────────────────────────────────────────────────────────────

interface Conversation {
  id: string;
  external_user_id: string;
  bsuid: string | null;
  status: string;
  last_message_at: string | null;
  created_at: string;
  follow_count: number | null;
  follow_finalizado: boolean | null;
  follow_finalizado_motivo: string | null;
  follow_finalizado_em: string | null;
  follow_ultima_interacao: string | null;
  lead_name: string | null;
  lead_username: string | null;
  lead_ctwa: string | null;
  lead_first_seen_at: string | null;
  lead_last_seen_at: string | null;
}

// ── FollowupBadge ─────────────────────────────────────────────────────────────

function FollowupBadge({ conv }: { conv: Conversation }) {
  if (conv.follow_count === null) return <span style={{ color: "var(--line-max)", fontSize: 12 }}>—</span>;
  if (conv.follow_finalizado)
    return (
      <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: "var(--surface-hover)", color: "var(--ink-muted)" }}>
        ✓ finalizado
        {conv.follow_finalizado_motivo && ` · ${conv.follow_finalizado_motivo}`}
        {conv.follow_finalizado_em && ` · ${fmtDate(conv.follow_finalizado_em)}`}
      </span>
    );
  return (
    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: "rgba(108,71,255,0.15)", color: "var(--brand)" }}>
      {conv.follow_count}× enviado
    </span>
  );
}

// ── TabConversas ──────────────────────────────────────────────────────────────

export function TabConversas({ agent }: { agent: any }) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  // Busca no servidor (o endpoint lista só as 100 mais recentes — filtrar
  // client-side escondia conversa antiga). Debounce pra não buscar por tecla.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data: conversations = [], isLoading, refetch } = useQuery<Conversation[]>({
    queryKey: ["conversations", agent.id, debouncedSearch],
    queryFn: () => api.get(`/agents/${agent.id}/conversations${debouncedSearch ? `?q=${encodeURIComponent(debouncedSearch)}` : ""}`),
    refetchInterval: 10000,
    placeholderData: (prev) => prev,
  });

  const filtered = conversations.filter(c => {
    if (!search) return true;
    const digits = search.replace(/\D/g, "");
    const q = search.toLowerCase();
    const byPhone = digits !== "" && c.external_user_id.includes(digits);
    const byBsuid = !!c.bsuid && c.bsuid.toLowerCase().includes(q);
    const byName = !!c.lead_name && c.lead_name.toLowerCase().includes(q);
    const byUser = !!c.lead_username && c.lead_username.toLowerCase().includes(q);
    return byPhone || byBsuid || byName || byUser;
  });

  const selected = conversations.find(c => c.id === selectedId) ?? null;

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
      <style>{`@keyframes convModalIn{from{opacity:0;transform:scale(0.96) translateY(6px)}to{opacity:1;transform:scale(1) translateY(0)}}`}</style>

      {/* Toolbar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "14px 24px", borderBottom: "1px solid var(--surface-input)", flexShrink: 0,
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, marginRight: 4 }}>Conversas</span>

        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar nome, número ou bsuid..."
          style={{
            background: "var(--surface-hover)", border: "1px solid var(--line-strong)", borderRadius: 8,
            padding: "6px 12px", fontSize: 12, color: "var(--ink)", outline: "none", width: 180,
          }}
        />

        <button
          onClick={() => refetch()}
          style={{
            marginLeft: "auto", fontSize: 12, color: "var(--ink-secondary)",
            padding: "6px 12px", borderRadius: 8,
            border: "1px solid var(--line-strong)", display: "flex", alignItems: "center", gap: 5,
            background: "transparent",
          }}
          onMouseOver={e => { e.currentTarget.style.borderColor = "var(--brand)"; e.currentTarget.style.color = "var(--brand)"; }}
          onMouseOut={e => { e.currentTarget.style.borderColor = "var(--line-strong)"; e.currentTarget.style.color = "var(--ink-secondary)"; }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="23 4 23 10 17 10"/>
            <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
          </svg>
          Atualizar
        </button>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {isLoading ? (
          <div style={{ padding: 24, color: "var(--ink-dim)", fontSize: 13 }}>Carregando...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 24, color: "var(--ink-dim)", fontSize: 13 }}>
            {search ? "Nenhuma conversa encontrada." : "Nenhuma conversa ainda."}
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ position: "sticky", top: 0, background: "var(--surface-app)", zIndex: 1 }}>
              <tr style={{ borderBottom: "1px solid var(--surface-input)" }}>
                {(["CONTATO", "ÚLTIMA MENSAGEM", "FOLLOWUP", ""] as const).map((h, i) => (
                  <th key={i} style={{
                    padding: `10px ${i === 0 ? "24px" : "16px"}`,
                    fontSize: 11, fontWeight: 600, color: "var(--ink-muted)",
                    textTransform: "uppercase", letterSpacing: "0.04em", textAlign: "left",
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(conv => (
                <tr
                  key={conv.id}
                  onClick={() => setSelectedId(conv.id)}
                  style={{ borderBottom: "1px solid var(--surface-hover)", cursor: "pointer", transition: "background 0.1s" }}
                  onMouseOver={e => (e.currentTarget.style.background = "var(--surface-hover)")}
                  onMouseOut={e => (e.currentTarget.style.background = "")}
                >
                  {/* CONTATO — nome/username do lead + telefone (ou bsuid) */}
                  <td style={{ padding: "12px 24px" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)" }}>
                        {conv.lead_name || conv.lead_username || fmtPhone(conv.external_user_id) || conv.bsuid || "—"}
                      </span>
                      {(conv.lead_name || conv.lead_username) && (
                        <span style={{ fontSize: 11, color: "var(--ink-dim)" }}>
                          {conv.external_user_id ? fmtPhone(conv.external_user_id) : conv.bsuid}
                        </span>
                      )}
                    </div>
                  </td>

                  {/* ÚLTIMA MENSAGEM */}
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "var(--ink-secondary)", whiteSpace: "nowrap" }}>
                    {fmtDate(conv.last_message_at)}
                  </td>

                  {/* FOLLOWUP */}
                  <td style={{ padding: "12px 16px" }}>
                    <FollowupBadge conv={conv} />
                  </td>

                  {/* chevron */}
                  <td style={{ padding: "12px 16px", textAlign: "right" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: "var(--ink-dim)" }}>
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal universal */}
      {selected && (
        <ConversationModal
          agentId={agent.id}
          convId={selected.id}
          phone={selected.external_user_id}
          bsuid={selected.bsuid}
          lead={{
            name: selected.lead_name,
            username: selected.lead_username,
            ctwa: selected.lead_ctwa,
            firstSeenAt: selected.lead_first_seen_at,
            lastSeenAt: selected.lead_last_seen_at,
          }}
          createdAt={selected.created_at}
          lastMessageAt={selected.last_message_at ?? undefined}
          followup={{
            count: selected.follow_count,
            finalizado: selected.follow_finalizado,
            motivo: selected.follow_finalizado_motivo,
            em: selected.follow_finalizado_em,
          }}
          autoRefreshMs={8000}
          variant="whatsapp"
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}
